import * as React from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { StatusBadge } from "@/components/ui/status"
import { api } from "@/lib/api"
import { useNotificationStore } from "@/store"
import {
  Clock,
  Play,
  Pause,
  Trash2,
  Plus,
  CheckCircle,
  XCircle,
  RefreshCw,
  Calendar,
  Timer,
  Zap,
} from "lucide-react"

function JobScheduler() {
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)

  const { data: jobs, isLoading } = useQuery({
    queryKey: ["jobs"],
    queryFn: () => api.get("/jobs"),
  })

  const { data: stats } = useQuery({
    queryKey: ["job-stats"],
    queryFn: () => api.get("/jobs/stats"),
  })

  const runMutation = useMutation({
    mutationFn: (id) => api.post(`/jobs/${id}/run`),
    onSuccess: () => {
      queryClient.invalidateQueries(["jobs"])
      addNotification({ type: "success", message: "Job started successfully" })
    },
    onError: (error) => {
      addNotification({ type: "error", message: error.message })
    },
  })

  const toggleMutation = useMutation({
    mutationFn: (id) => api.post(`/jobs/${id}/toggle`),
    onSuccess: () => {
      queryClient.invalidateQueries(["jobs"])
      addNotification({ type: "success", message: "Job status updated" })
    },
  })

  const formatSchedule = (schedule) => {
    if (!schedule) return "Manual"
    if (schedule.startsWith("every_")) {
      const match = schedule.match(/every_(\d+)(m|h|d)/)
      if (match) {
        const [, amount, unit] = match
        const units = { m: "minutes", h: "hours", d: "days" }
        return `Every ${amount} ${units[unit]}`
      }
    }
    if (schedule.startsWith("cron:")) {
      return `Cron: ${schedule.replace("cron:", "")}`
    }
    return schedule
  }

  const jobColors = {
    scan_stable: "info",
    scan_ip: "info",
    update_subscriptions: "info",
    backup: "success",
    cleanup_logs: "warning",
    check_panels: "info",
    post_proxies: "info",
    cleanup_cache: "info",
    warmup_cache: "info",
    sync_panel_users: "info",
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Job Scheduler</h1>
          <p className="text-muted-foreground">Manage and monitor scheduled jobs</p>
        </div>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          New Job
        </Button>
      </div>

      {/* Stats */}
      {stats?.data && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Jobs</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.data.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Enabled</CardTitle>
              <CheckCircle className="h-4 w-4 text-success" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-success">{stats.data.enabled}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Recent Runs</CardTitle>
              <Play className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.data.recentRuns?.length || 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Failed</CardTitle>
              <XCircle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">
                {stats.data.recentRuns?.filter(r => r.status === "failed").length || 0}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Jobs Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {jobs?.data?.map((job) => (
          <Card key={job.id} className="relative">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{job.name}</CardTitle>
              <Badge variant={jobColors[job.type] || "secondary"}>
                {job.type}
              </Badge>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">{job.description}</p>
                
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>{formatSchedule(job.schedule)}</span>
                </div>

                {job.last_run && (
                  <div className="flex items-center gap-2 text-sm">
                    <Timer className="h-4 w-4 text-muted-foreground" />
                    <span>Last: {new Date(job.last_run * 1000).toLocaleString()}</span>
                    <StatusBadge status={job.last_status === "success" ? "ok" : "error"} />
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <StatusBadge status={job.is_enabled ? "ok" : "offline"} />
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => runMutation.mutate(job.id)}
                      disabled={runMutation.isPending}
                    >
                      <Zap className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleMutation.mutate(job.id)}
                    >
                      {job.is_enabled ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Runs */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Job Runs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {stats?.data?.recentRuns?.map((run, index) => (
              <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <StatusBadge status={run.status === "success" ? "ok" : "error"} />
                  <div>
                    <p className="font-medium">{run.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(run.started_at * 1000).toLocaleString()}
                    </p>
                  </div>
                </div>
                {run.error && (
                  <p className="text-sm text-destructive truncate max-w-xs">{run.error}</p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default JobScheduler
